import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ProductDetailModal.css";
import { productHasProteinBreadChoice, PROTEIN_BREAD_BADGE_SRC } from "../utils/productBadges";

const decodeHTMLEntities = (text) => {
  if (!text) return "";
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  const decoded = textarea.value;
  return decoded.replace(/<[^>]+>/g, "");
};

const getInitialVariant = (product, cartItem) => {
  const variantGroup = product?.variations?.find(v => v.isVariantGroup);
  const options = variantGroup?.options || [];
  if (options.length === 0) return null;

  if (cartItem) {
    const match = options.find((option) => (
      option.id === cartItem.itemId ||
      option.skuCode === cartItem.skuCode ||
      option.title === cartItem.variantTitle
    ));
    if (match) return match;
  }

  return options[0];
};

export default function ProductDetailModal({
  product,
  onClose,
  onAddToCart,
  onReplaceCartItem,
  soldOutItems = {},
  compactMode = false,
  initialCartItem = null,
}) {
  const [show, setShow] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(compactMode);
  const [modalQty, setModalQty] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(() => getInitialVariant(product, initialCartItem));
  const [selectedOptionsByGroup, setSelectedOptionsByGroup] = useState({});
  const modalContentRef = useRef(null);
  // Height of the sheet, so the phone close button can sit just above its top edge.
  const [sheetHeight, setSheetHeight] = useState(0);
  const modalBodyRef = useRef(null);
  const modalNameRef = useRef(null);
  const stickyActiveRef = useRef(false);

  if (!product) return null;

  const isCartMode = Boolean(onAddToCart);

  useEffect(() => {
    const sheet = modalContentRef.current;
    if (!sheet) return undefined;
    const measure = () => setSheetHeight(sheet.offsetHeight);
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(sheet);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    setModalQty(Math.max(1, Math.floor(Number(initialCartItem?.quantity) || 1)));
  }, [product?.id, initialCartItem?.id]);

  const isSoldOut = product.skuCode && (product.skuCode in soldOutItems);
  const isOptionSoldOut = (option) => Boolean(option?.skuCode && (option.skuCode in soldOutItems));

  const variantGroup = product.variations?.find(v => v.isVariantGroup);
  const hasVariants = variantGroup && variantGroup.options.length > 1;
  const hasSingleVariant = variantGroup && variantGroup.options.length === 1;

  const optionGroups = useMemo(
    () => product.variations?.filter(v => !v.isVariantGroup) || [],
    [product]
  );

  const selectedVariantOptionSets = useMemo(() => (
    selectedVariant && selectedVariant.optionSets && selectedVariant.optionSets.length > 0
      ? selectedVariant.optionSets
      : []
  ), [selectedVariant]);

  const displayPrice = (hasVariants || hasSingleVariant) && selectedVariant ? selectedVariant.price : product.price;
  const shouldShowStartingAtText = hasVariants && !selectedVariant;

  const cleanDescription = decodeHTMLEntities(product.description);
  const hasProteinBreadChoice = productHasProteinBreadChoice(product);
  const productTags = product.tags || [];

  let validNutritions =
    Array.isArray(product.nutritions) && product.nutritions.length > 0
      ? product.nutritions.filter((nutri) => nutri.nutritionName && nutri.nutritionValue)
      : [];

  const portionSizeEntry = validNutritions.find((nutri) => nutri.nutritionName.toLowerCase() === "portionsize");
  const portionSizeUnitEntry = validNutritions.find((nutri) => nutri.nutritionName.toLowerCase() === "portionsizeunit");

  if (portionSizeEntry && portionSizeUnitEntry) {
    validNutritions = validNutritions.filter(
      (nutri) => nutri.nutritionName.toLowerCase() !== "portionsize" && nutri.nutritionName.toLowerCase() !== "portionsizeunit"
    );
    validNutritions.unshift({
      nutritionName: "Portion",
      nutritionValue: `${portionSizeEntry.nutritionValue} ${portionSizeUnitEntry.nutritionValue}`,
    });
  }

  const allergyInfoIndex = validNutritions.findIndex((nutri) => nutri.nutritionName.toLowerCase() === "allergyinformation");
  if (allergyInfoIndex !== -1) {
    const allergyInfo = validNutritions[allergyInfoIndex];
    validNutritions[allergyInfoIndex] = {
      ...allergyInfo,
      nutritionValue: allergyInfo.nutritionValue
        .split(',').map(item => item.trim())
        .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
        .join(', '),
    };
  }

  const fallbackImages = [
    "https://images.unsplash.com/photo-1509042239860-f550ce710b93?fit=crop&w=400&h=225&q=80",
    "https://images.unsplash.com/photo-1511920170033-f8396924c348?fit=crop&w=400&h=225&q=80",
    "https://images.unsplash.com/photo-1523473827532-4d1e1a2f1c2b?fit=crop&w=400&h=225&q=80",
  ];

  const hasProductImage = Boolean(product.imageUrl || product.productDefaultImage || product.categoryImage);
  const imageSrc =
    product.imageUrl || product.productDefaultImage || product.categoryImage ||
    fallbackImages[Math.floor(Math.random() * fallbackImages.length)];

  useEffect(() => {
    if (!variantGroup || !variantGroup.options || variantGroup.options.length === 0) {
      setSelectedVariant(null);
      return;
    }
    setSelectedVariant((prev) => {
      const initialVariant = getInitialVariant(product, initialCartItem);
      if (initialCartItem && initialVariant) return initialVariant;
      if (prev && variantGroup.options.some((option) => option.id === prev.id)) {
        const prevOption = variantGroup.options.find((option) => option.id === prev.id);
        const prevSoldOut = prevOption?.skuCode && (prevOption.skuCode in soldOutItems);
        if (!prevSoldOut) return prev;
      }
      const firstAvailable = variantGroup.options.find(
        (option) => !(option.skuCode && (option.skuCode in soldOutItems))
      );
      return initialVariant || firstAvailable || variantGroup.options[0];
    });
  }, [product, variantGroup, initialCartItem?.id, soldOutItems]);

  const selectedVariantSoldOut = Boolean(selectedVariant?.skuCode && (selectedVariant.skuCode in soldOutItems));

  const getOptionExtraPrice = (option) => {
    const optionPrice = Number(option?.optionPrice);
    if (Number.isFinite(optionPrice)) return optionPrice;
    const basePrice = Number(option?.basePrice);
    const totalPrice = Number(option?.price);
    if (Number.isFinite(totalPrice) && Number.isFinite(basePrice)) return totalPrice - basePrice;
    return 0;
  };

  const activeOptionGroups = useMemo(() => (
    selectedVariant && selectedVariantOptionSets.length > 0
      ? selectedVariantOptionSets
      : (!variantGroup ? optionGroups : [])
  ), [optionGroups, selectedVariant, selectedVariantOptionSets, variantGroup]);

  useEffect(() => {
    if (!Array.isArray(activeOptionGroups) || activeOptionGroups.length === 0) {
      setSelectedOptionsByGroup({});
      return;
    }

    setSelectedOptionsByGroup((prev) => {
      const next = {};
      const initialOptionsByGroup = (initialCartItem?.selectedOptions || []).reduce((acc, option) => {
        if (option?.groupId && option?.id) {
          if (!acc[option.groupId]) acc[option.groupId] = [];
          acc[option.groupId].push(option.id);
        }
        return acc;
      }, {});

      activeOptionGroups.forEach((group) => {
        const groupId = group.optionSetId;
        const min = Number(group.min || 0);
        const max = Number.isFinite(Number(group.max)) ? Number(group.max) : Infinity;
        const validOptionIds = new Set((group.options || []).map((option) => option.id));

        const sourceSelections = initialCartItem ? initialOptionsByGroup[groupId] : prev[groupId];
        let selections = Array.isArray(sourceSelections)
          ? sourceSelections.filter((id) => validOptionIds.has(id))
          : [];

        if (selections.length < min) {
          const defaultIds = (group.options || [])
            .filter((option) => option.isDefault)
            .filter((option) => !isOptionSoldOut(option))
            .map((option) => option.id);
          const fallbackIds = (group.options || [])
            .filter((option) => !isOptionSoldOut(option))
            .map((option) => option.id);
          const source = [...defaultIds, ...fallbackIds];
          source.forEach((optionId) => {
            if (selections.length >= min || selections.length >= max) return;
            if (!selections.includes(optionId)) selections.push(optionId);
          });
        }

        next[groupId] = selections.slice(0, Number.isFinite(max) ? max : selections.length);
      });

      return next;
    });
  }, [activeOptionGroups, initialCartItem?.id]);

  const toggleOptionSelection = (group, option) => {
    if (isOptionSoldOut(option)) return;
    const groupId = group.optionSetId;
    const min = Number(group.min || 0);
    const max = Number.isFinite(Number(group.max)) ? Number(group.max) : Infinity;

    setSelectedOptionsByGroup((prev) => {
      const current = Array.isArray(prev[groupId]) ? prev[groupId] : [];
      const isSelected = current.includes(option.id);
      let nextSelection = [...current];

      if (max === 1) {
        nextSelection = isSelected ? (min === 0 ? [] : [option.id]) : [option.id];
      } else if (isSelected) {
        if (current.length > min) nextSelection = current.filter((id) => id !== option.id);
      } else if (current.length < max) {
        nextSelection = [...current, option.id];
      }

      return { ...prev, [groupId]: nextSelection };
    });
  };

  const selectedOptions = activeOptionGroups.flatMap((group) => {
    const selectedIds = selectedOptionsByGroup[group.optionSetId] || [];
    return (group.options || [])
      .filter((option) => selectedIds.includes(option.id))
      .filter((option) => !isOptionSoldOut(option))
      .map((option) => ({
        id: option.id,
        optionId: option.optionId || option.id,
        itemId: option.itemId || "",
        skuCode: option.skuCode || "",
        name: option.name || option.title,
        groupName: group.optionSetName || group.optionSetId,
        groupId: group.optionSetId,
        title: option.title,
        price: getOptionExtraPrice(option),
        unitPrice: getOptionExtraPrice(option),
      }));
  });

  const selectedOptionsExtra = selectedOptions.reduce((sum, option) => sum + Number(option.price || 0), 0);
  const finalDisplayPrice = displayPrice + selectedOptionsExtra;
  const hasVariantSpecificOptionSets = selectedVariant && selectedVariantOptionSets.length > 0;
  const shouldRenderVariations = hasVariants || hasVariantSpecificOptionSets || (!variantGroup && optionGroups.length > 0);

  const hasMissingRequiredOptions = activeOptionGroups.some((group) => {
    const min = Number(group.min || 0);
    const selectedIds = (selectedOptionsByGroup[group.optionSetId] || []);
    const availableOptions = (group.options || []).filter((opt) => !isOptionSoldOut(opt));
    if (availableOptions.length === 0 && min > 0) return true;
    return selectedIds.length < min;
  });

  const canAddToCart = !isSoldOut && !selectedVariantSoldOut && !hasMissingRequiredOptions;

  const handleAddToCart = () => {
    if (!canAddToCart || !onAddToCart) return;
    const chosenVariant = (hasVariants || hasSingleVariant) ? selectedVariant : null;
    if (initialCartItem && onReplaceCartItem) {
      onReplaceCartItem(initialCartItem.id, product, chosenVariant, selectedOptions, modalQty);
      handleClose();
      return;
    }
    onAddToCart(product, chosenVariant, selectedOptions, modalQty);
    handleClose();
  };

  const handleStepModalQty = (delta) => {
    setModalQty((prev) => Math.max(1, prev + delta));
  };

  useEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    body.classList.add('modal-open');
    html.classList.add('modal-open');
    const timer = setTimeout(() => setShow(true), 10);
    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overflow = '';
      body.classList.remove('modal-open');
      html.classList.remove('modal-open');
      window.scrollTo(0, scrollY);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const handleEscKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, []);

  useEffect(() => {
    const modalContent = modalContentRef.current;
    const modalBody = modalBodyRef.current;
    const modalName = modalNameRef.current;
    if (!modalContent || !modalBody || !modalName) return undefined;

    stickyActiveRef.current = false;
    setShowStickyHeader(false);
    let rafId = null;

    const getScrollContainer = () => (window.innerWidth > 650 ? modalBody : modalContent);

    const updateStickyHeader = () => {
      const scrollContainer = getScrollContainer();
      const containerRect = scrollContainer.getBoundingClientRect();
      const nameRect = modalName.getBoundingClientRect();
      const isNameAboveViewport = nameRect.bottom <= containerRect.top + 8;
      const isNameWellVisible = nameRect.top >= containerRect.top + 40;
      if (!stickyActiveRef.current && isNameAboveViewport) {
        stickyActiveRef.current = true;
        setShowStickyHeader(true);
      } else if (stickyActiveRef.current && isNameWellVisible) {
        stickyActiveRef.current = false;
        setShowStickyHeader(false);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => { rafId = null; updateStickyHeader(); });
    };

    const handleResize = () => {
      if (rafId !== null) { window.cancelAnimationFrame(rafId); rafId = null; }
      stickyActiveRef.current = false;
      setShowStickyHeader(false);
      modalContent.removeEventListener('scroll', handleScroll);
      modalBody.removeEventListener('scroll', handleScroll);
      const scrollContainer = getScrollContainer();
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      window.requestAnimationFrame(() => updateStickyHeader());
    };

    const scrollContainer = getScrollContainer();
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    window.requestAnimationFrame(() => updateStickyHeader());

    return () => {
      modalContent.removeEventListener('scroll', handleScroll);
      modalBody.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [product]);

  const handleTouchStart = (e) => {
    if (window.innerWidth > 650) return;
    setDragStart(e.touches[0].clientY);
    if (e.currentTarget.scrollTop <= 0) setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (window.innerWidth > 650) return;
    const scrollTop = e.currentTarget.scrollTop;
    const diff = e.touches[0].clientY - dragStart;
    if (scrollTop <= 0 && diff > 0) {
      if (isDragging) setDragOffset(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging || window.innerWidth > 650) return;
    setIsDragging(false);
    if (dragOffset > 100) handleClose();
    setDragOffset(0);
  };

  const handleClose = () => {
    setIsClosing(true);
    setShow(false);
    setTimeout(() => onClose(), 400);
  };

  const handleOverlayClick = (e) => { if (e.target === e.currentTarget) handleClose(); };

  const toTitleCase = (str) => str.replace(/([A-Z])/g, ' $1').replace(/^./, (m) => m.toUpperCase()).trim();

  const overlayOpacity = dragOffset > 0 ? Math.max(0, 1 - dragOffset / 300) : 1;

  const renderOptionSetMeta = (group) => {
    if (!group) return null;
    const min = Number(group.min || 0);
    const maxRaw = Number(group.max);
    const max = Number.isFinite(maxRaw) ? maxRaw : Infinity;
    const required = min > 0;
    let countLabel = "";
    if (Number.isFinite(max) && max === 1 && min === 1) countLabel = "Choose 1";
    else if (Number.isFinite(max) && min === 0) countLabel = `Select up to ${max}`;
    else if (Number.isFinite(max)) countLabel = `Select ${min}-${max}`;
    else if (min > 0) countLabel = `Select ${min}+`;
    const label = required ? "Required" : "Optional";
    return (
      <span className="option-set-meta">
        {label}{countLabel ? ` • ${countLabel}` : ""}
      </span>
    );
  };

  const renderOptionItem = (option, group) => {
    const hasEggTag = option.tags?.some(tag => tag?.name?.toLowerCase().includes('egg'));
    const hasNonVegTag = option.tags?.some(tag => {
      const name = tag?.name?.toLowerCase() || '';
      return name.includes('non') && name.includes('veg');
    });
    const hasEggAndNonVeg = hasEggTag && hasNonVegTag;
    const selectedIds = selectedOptionsByGroup[group.optionSetId] || [];
    const isSelected = selectedIds.includes(option.id);
    const extraPrice = getOptionExtraPrice(option);
    const optionSoldOut = isOptionSoldOut(option);

    return (
      <div
        key={option.id}
        className={`variation-item ${isCartMode ? "option-selectable" : ""} ${(isCartMode && !optionSoldOut && isSelected) ? "selected" : ""} ${optionSoldOut ? "option-sold-out" : ""}`}
        onClick={(isCartMode && !optionSoldOut) ? () => toggleOptionSelection(group, option) : undefined}
        role={isCartMode ? "button" : undefined}
        tabIndex={isCartMode ? 0 : undefined}
        aria-disabled={optionSoldOut || undefined}
        onKeyDown={isCartMode && !optionSoldOut ? (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleOptionSelection(group, option);
          }
        } : undefined}
      >
        <div className="variation-header">
          <div className="variation-title-with-tags">
            {option.tags && option.tags.length > 0 && (
              <div className="option-tags-container">
                {hasEggTag && (
                  <img src="/EggTag.png" alt="Egg (Non-Veg)" className="option-tag-icon egg-icon" />
                )}
                {option.tags.map((tag, idx) => {
                  if (hasEggTag && tag?.name?.toLowerCase().includes('egg')) return null;
                  if (hasEggAndNonVeg && (
                    (tag?.name?.toLowerCase().includes('non') && tag?.name?.toLowerCase().includes('veg'))
                  )) return null;
                  return tag?.tagIcon && (
                    <img key={idx} src={tag.tagIcon.fullPath} alt={tag.name} className="option-tag-icon" />
                  );
                })}
              </div>
            )}
            <span className="variation-title">{option.title}</span>
          </div>
          <span className="variation-price">
            {optionSoldOut ? (
              <span className="option-sold-out-text">Sold Out</span>
            ) : (
              option.isOptionSet ? (
                extraPrice > 0 ? `+₹${Math.round(extraPrice)}` : ''
              ) : (
                `₹${Math.round(option.price)}`
              )
            )}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`modal-overlay ${show ? "show" : ""} ${isClosing ? "closing" : ""}`}
      onClick={handleOverlayClick}
      style={{ backgroundColor: `rgba(0, 0, 0, ${0.5 * overlayOpacity})` }}
    >
      {/* Phones: centred just above the sheet's top edge, following its height (and the drag).
          Tablet/desktop keep their own fixed placement and ignore these two variables. */}
      <button
        type="button"
        className={`modal-close-btn ${compactMode ? "compact-mode" : ""}`}
        onClick={handleClose}
        aria-label="Close"
        style={{ "--sheet-h": `${sheetHeight}px`, "--sheet-drag": `${dragOffset}px` }}
      >
        ×
      </button>
      <div
        className={`modal-content ${show ? "show" : ""} ${isClosing ? "closing" : ""} ${isDragging ? "dragging" : ""} ${!hasProductImage ? "no-image" : ""} ${compactMode ? "compact-mode" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        ref={modalContentRef}
        style={{
          transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
          transition: isDragging ? 'none' : undefined,
        }}
      >
        <div className="modal-drag-handle" aria-hidden="true" />
        {!compactMode && hasProductImage && (
          <div className="modal-image-container">
            {isSoldOut && <div className="modal-sold-out-badge-floating">Sold Out</div>}
            <img src={imageSrc} alt={product.name} className="modal-image" onError={(e) => (e.target.src = fallbackImages[0])} />
            {isSoldOut && <div className="modal-sold-out-overlay"></div>}
          </div>
        )}

        <div className="modal-detail-panel">
          <div className="modal-body" ref={modalBodyRef}>
            <div className={`sticky-header-wrapper ${(showStickyHeader || compactMode) ? "visible" : ""}`}>
              <div
                className={`sticky-header ${(showStickyHeader || compactMode) ? "visible" : ""}`}
                aria-hidden={!(showStickyHeader || compactMode)}
              >
                {hasProductImage && (
                  <img src={imageSrc} alt={product.name} className="sticky-header-image" onError={(e) => (e.target.src = fallbackImages[0])} />
                )}
                <div className="sticky-header-info">
                  <h3 className="sticky-header-title">{product.name}</h3>
                  <p className="sticky-header-price">
                    {shouldShowStartingAtText && <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginRight: '4px' }}>Starting at</span>}
                    ₹{Math.round(isCartMode ? finalDisplayPrice : displayPrice)}
                  </p>
                </div>
                {((productTags.length > 0) || hasProteinBreadChoice) && (() => {
                  const hasEggTag = productTags.some(tag => tag?.name?.toLowerCase().includes('egg'));
                  const hasNonVegTag = productTags.some(tag => {
                    const name = tag?.name?.toLowerCase() || '';
                    return name.includes('non') && name.includes('veg');
                  });
                  return (
                    <div className="sticky-header-tags">
                      {hasEggTag ? <img src="/EggTag.png" alt="Egg (Non-Veg)" className="sticky-header-tag-icon" /> : ''}
                      {productTags.map((tag, index) => {
                        if (hasEggTag && tag?.name?.toLowerCase() === 'egg') return null;
                        if (hasEggTag && hasNonVegTag && tag?.name?.toLowerCase().includes('non')) return null;
                        if (tag?.id === '6932636dec4624b7afca0266') {
                          return <img key={index} src="/NewTag.png" alt={tag?.name || 'New'} className="sticky-header-tag-icon" />;
                        }
                        return tag?.tagIcon ? <img key={index} src={tag.tagIcon.fullPath} alt={tag?.name} className="sticky-header-tag-icon" /> : null;
                      })}
                      {hasProteinBreadChoice && (
                        <img src={PROTEIN_BREAD_BADGE_SRC} alt="Protein Bread" className="sticky-header-protein-tag-icon" />
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {!compactMode && (
              <>
                {((productTags.length > 0) || hasProteinBreadChoice) && (() => {
                  const hasEggTag = productTags.some(tag => tag?.name?.toLowerCase().includes('egg'));
                  const hasNonVegTag = productTags.some(tag => {
                    const name = tag?.name?.toLowerCase() || '';
                    return name.includes('non') && name.includes('veg');
                  });
                  return (
                    <div className="modal-tags-container">
                      {hasEggTag ? (
                        <div className="modal-egg-nonveg-container">
                          <img src="/EggTag.png" alt="Egg (Non-Veg)" className="modal-egg-nonveg-icon" />
                        </div>
                      ) : ''}
                      {productTags.map((tag, index) => {
                        if (hasEggTag && tag?.name?.toLowerCase() === 'egg') return null;
                        if (hasEggTag && hasNonVegTag && tag?.name?.toLowerCase().includes('non')) return null;
                        if (tag?.id === '6932636dec4624b7afca0266') {
                          return (
                            <div key={index} className="modal-egg-nonveg-container">
                              <img src="/NewTag.png" alt={tag?.name || 'New'} className="modal-egg-nonveg-icon" />
                            </div>
                          );
                        }
                        return tag?.tagIcon ? <img key={index} src={tag.tagIcon.fullPath} alt={tag?.name} className="modal-tag-icon" /> : null;
                      })}
                      {hasProteinBreadChoice && (
                        <div className="modal-protein-tag-container">
                          <img src={PROTEIN_BREAD_BADGE_SRC} alt="Protein Bread" className="modal-egg-nonveg-icon" />
                        </div>
                      )}
                    </div>
                  );
                })()}

                <h2 className="modal-name" ref={modalNameRef}>{product.name}</h2>

                <p className="modal-price">
                  {shouldShowStartingAtText && <span style={{ fontSize: '0.85em', fontWeight: 'normal', marginRight: '6px' }}>Starting at</span>}
                  ₹{Math.round(isCartMode ? finalDisplayPrice : displayPrice)}
                </p>

                {cleanDescription && (
                  <div className="modal-description-container">
                    <p className="modal-description">{cleanDescription}</p>
                  </div>
                )}

                {validNutritions.length > 0 && (
                  <div className="modal-nutrition">
                    <h4>Nutrition Facts</h4>
                    <table className="nutrition-table">
                      <tbody>
                        {validNutritions.map((nutri, idx) => (
                          <tr key={idx}>
                            <td className="nutri-name">{toTitleCase(nutri.nutritionName)}</td>
                            <td className="nutri-value">
                              {nutri.nutritionName === "Portion" || nutri.nutritionName.toLowerCase() === "allergyinformation"
                                ? nutri.nutritionValue
                                : nutri.units ? `${nutri.nutritionValue}${nutri.units}` : `${nutri.nutritionValue}${nutri.nutritionName.toLowerCase() === 'sodium' ? 'm' : ''}g`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {shouldRenderVariations && (
              <div className="modal-variations">
                {hasVariants && <h4>Available Options</h4>}
                <div className="variations-list">
                  {hasVariants && (
                    <div className="variation-group">
                      <div className="option-set-header">
                        <h5 className="option-set-name">{variantGroup.optionSetName}</h5>
                        {isCartMode && <span className="option-set-meta">Required • Choose 1</span>}
                      </div>
                      <div className="variant-selector">
                        {variantGroup.options.map((variant) => {
                          const variantSoldOut = Boolean(variant.skuCode && (variant.skuCode in soldOutItems));
                          return (
                            <button
                              key={variant.id}
                              className={`variant-button ${selectedVariant?.id === variant.id ? 'active' : ''} ${variantSoldOut ? 'variant-sold-out' : ''}`}
                              onClick={variantSoldOut ? undefined : () => setSelectedVariant(variant)}
                              disabled={variantSoldOut}
                            >
                              <span className="variant-button-title">{variant.title}</span>
                              {variantSoldOut ? (
                                <span className="variant-button-price variant-sold-out-text">Sold Out</span>
                              ) : (
                                <span className="variant-button-price">₹{Math.round(variant.price)}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {hasVariantSpecificOptionSets && (
                    <>
                      {selectedVariantOptionSets.map((optionGroup) => (
                        <div key={optionGroup.optionSetId} className="variation-group">
                          <div className="option-set-header">
                            <h5 className="option-set-name">{optionGroup.optionSetName}</h5>
                            {isCartMode && renderOptionSetMeta(optionGroup)}
                          </div>
                          <div className="options-group">
                            {optionGroup.options.map((option) => renderOptionItem(option, optionGroup))}
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {!variantGroup && optionGroups.map((optionGroup) => (
                    <div key={optionGroup.optionSetId} className="variation-group">
                      <div className="option-set-header">
                        <h5 className="option-set-name">{optionGroup.optionSetName}</h5>
                        {isCartMode && renderOptionSetMeta(optionGroup)}
                      </div>
                      <div className="options-group">
                        {optionGroup.options.map((option) => renderOptionItem(option, optionGroup))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {isCartMode && (
            <div className="modal-sticky-cart-bar">
              <div className="modal-sticky-qty-controls">
                <button
                  className="modal-sticky-qty-btn"
                  onClick={() => handleStepModalQty(-1)}
                  aria-label={`Remove one ${product.name}`}
                  disabled={!canAddToCart || modalQty <= 1}
                >
                  −
                </button>
                <span className="modal-sticky-qty-value">{modalQty}</span>
                <button
                  className="modal-sticky-qty-btn"
                  onClick={() => handleStepModalQty(1)}
                  aria-label={`Add one ${product.name}`}
                  disabled={!canAddToCart}
                >
                  +
                </button>
              </div>
              <button
                className="modal-add-cart-btn sticky"
                onClick={handleAddToCart}
                disabled={!canAddToCart}
              >
                {canAddToCart
                  ? `${initialCartItem ? "Update item" : "Add"} • ₹${Math.round(finalDisplayPrice * modalQty)}`
                  : "Unavailable"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
